import type { ThreadReplyOptions, TimelineEvent, TimelineEventContent } from "~/lib/matrix";

const MXID_PATTERN = /^@[^:\s]+:[^\s]+$/;
const HTML_FORMAT = "org.matrix.custom.html";

export interface RichThreadReply {
  raw: Record<string, unknown>;
  echo: TimelineEventContent;
  relation: NonNullable<TimelineEvent["threadRelation"]>;
}

export function hasRichThreadOptions(options: ThreadReplyOptions | undefined): boolean {
  if (!options) return false;
  const hasHtml = typeof options.formattedBody === "string" && options.formattedBody.length > 0;
  return hasHtml || (Array.isArray(options.mentions) && options.mentions.length > 0);
}

function uniqueMentions(mentions: readonly string[] | undefined): string[] {
  const unique: string[] = [];
  for (const userId of mentions ?? []) {
    if (typeof userId !== "string" || !MXID_PATTERN.test(userId)) {
      throw new Error(`sendThreadReply: invalid mention user id ${JSON.stringify(userId)}`);
    }
    if (!unique.includes(userId)) unique.push(userId);
  }
  return unique;
}

export function buildRichThreadReply(
  body: string,
  rootEventId: string,
  latestReplyId: string,
  options: ThreadReplyOptions,
): RichThreadReply {
  const userIds = uniqueMentions(options.mentions);
  const html =
    typeof options.formattedBody === "string" && options.formattedBody.length > 0
      ? options.formattedBody
      : undefined;
  const relation = {
    rel_type: "m.thread" as const,
    event_id: rootEventId,
    is_falling_back: true,
    "m.in_reply_to": { event_id: latestReplyId || rootEventId },
  };
  const raw: Record<string, unknown> = { msgtype: "m.text", body };
  if (html) {
    raw.format = HTML_FORMAT;
    raw.formatted_body = html;
  }
  if (userIds.length > 0) raw["m.mentions"] = { user_ids: userIds };
  raw["m.relates_to"] = relation;
  const echo: TimelineEventContent = {
    type: "m.text",
    body,
    ...(html ? { format: HTML_FORMAT, formattedBody: html } : {}),
    ...(userIds.length > 0 ? { mentions: { userIds, room: false } } : {}),
  };
  return { raw, echo, relation };
}
